/**
 * Chat attachments shown the way ChatGPT does: image thumbnails / PDF cards
 * above the composer while staged, and above the student's/teacher's own
 * message bubble once sent. Used by the teacher AI chat and the student tutor.
 *
 * Item shape: { id, kind: 'image' | 'pdf', filename, src?, fetchUrl? }
 *   src       — a URL the browser can load directly (data:/blob:)
 *   fetchUrl  — an /api path for a saved file; loaded with the auth token
 */
import React, { useEffect, useState } from 'react';
import { FileText, ImageIcon, Loader2, X } from 'lucide-react';

const SIZES = {
  sm: 'w-16 h-16',      // composer — square crop, like ChatGPT's composer
};
// Sent messages show the whole image at its own shape (no crop).
const LG_IMAGE = 'max-w-[240px] max-h-[200px] w-auto h-auto object-contain';
const LG_PLACEHOLDER = 'w-40 h-28';

function apiUrl(path) {
  return `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api${path}`;
}

/** Object URL for an authenticated image path; null while loading, 'error' if it failed. */
function useAuthedImage(fetchUrl) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!fetchUrl) return undefined;
    let cancelled = false;
    let objectUrl = null;
    const token = localStorage.getItem('desky_token');
    fetch(apiUrl(fetchUrl), { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        objectUrl = URL.createObjectURL(await res.blob());
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setUrl('error'); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fetchUrl]);
  return url;
}

function RemoveButton({ item, onRemove }) {
  if (!onRemove) return null;
  return (
    <button type="button" onClick={() => onRemove(item)} aria-label={`Remove ${item.filename}`}
      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center shadow hover:bg-black">
      <X className="w-3 h-3" />
    </button>
  );
}

function ImageTile({ item, size, onRemove }) {
  const loaded = useAuthedImage(item.src ? null : item.fetchUrl);
  const src = item.src || (loaded && loaded !== 'error' ? loaded : null);
  const failed = !item.src && (!item.fetchUrl || loaded === 'error');
  const lg = size === 'lg';
  const box = lg ? (src ? '' : LG_PLACEHOLDER) : SIZES.sm;
  return (
    <div className={`relative ${box} flex-shrink-0`} data-testid="chat-attachment-image">
      <div className={`${lg && src ? '' : 'w-full h-full'} rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center`}>
        {src && <img src={src} alt={item.filename} className={lg ? LG_IMAGE : 'w-full h-full object-cover'} />}
        {!src && !failed && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
        {failed && (
          <div className="flex flex-col items-center gap-1 px-1.5 text-center">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <span className="text-[9px] text-slate-500 leading-tight line-clamp-2 break-all">{item.filename}</span>
          </div>
        )}
      </div>
      <RemoveButton item={item} onRemove={onRemove} />
    </div>
  );
}

function PdfTile({ item, onRemove }) {
  return (
    <div className="relative flex-shrink-0" data-testid="chat-attachment-pdf">
      <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white pl-2 pr-3 py-2 max-w-[220px] shadow-sm">
        <span className="w-9 h-9 rounded-lg bg-rose-500 text-white flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-[12px] font-semibold text-slate-800 truncate">{item.filename}</span>
          <span className="block text-[10px] font-medium text-slate-500">PDF</span>
        </span>
      </div>
      <RemoveButton item={item} onRemove={onRemove} />
    </div>
  );
}

/**
 * @param {{ items: object[], size?: 'sm'|'lg', align?: 'start'|'end', onRemove?: (item) => void }} props
 */
export function AttachmentStrip({ items, size = 'sm', align = 'start', onRemove }) {
  if (!items || items.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${align === 'end' ? 'justify-end' : ''}`} data-testid="chat-attachments">
      {items.map((item, i) => (item.kind === 'pdf'
        ? <PdfTile key={item.id || i} item={item} onRemove={onRemove} />
        : <ImageTile key={item.id || i} item={item} size={size} onRemove={onRemove} />))}
    </div>
  );
}

/** Placeholder tile while a file is uploading. */
export function UploadingTile() {
  return (
    <div className={`${SIZES.sm} rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center`}
      data-testid="chat-attachment-uploading">
      <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
    </div>
  );
}
